import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import { ConfirmStatus } from "./ConfirmStatus";
import { ConfirmOrder } from "./ConfirmOrder";
import { NotificationType } from "../../../constants/global";
import { ToasterContext } from "../../../components/shared/Toaster/ToasterContext";
import { CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../../navigation/routeConfig";
import { FedexPickupType } from "../../../constants/shipping";
import { useMutationUpdateManyOrder } from "../../../queries/order/hooks/useMutationUpdateManyOrder";
import axios from "axios";

interface SellerConfirmationDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setSuccessOrderId: (successOrderId: null | number) => void;
  data: any;
  refetch: any;
  orderNumber: number;
}

const LOT_EXP_EDIT_INFO_PARAGRAPHS = [
  "Before confirming, verify each item's Lot Number and Expiration Date match the actual product being shipped. This is required for DSCSA compliance.",
  "If units have different lot numbers or expiration dates, use Split units to assign lot and expiration per unit.",
  "If you cannot fulfill the full quantity, decrease the quantity or mark units out of stock. The buyer will be notified of any quantity reduction.",
  "If the actual expiration date is shorter, mark the item Out of stock. Mismatched lot or expiration information may cause order return, penalties, or platform restriction.",
];

const needsConfirmationDetails = (item: any) => {
  if (item.status !== 1) return false;
  if (item.isSplit && item.splitUnits?.length) {
    return item.splitUnits.some((unit: any) => unit.available);
  }
  const originalQty = item.originalQuantity ?? item.quantity;
  const editedQty = item.editedQuantity ?? item.quantity;
  const hasQtyChange = editedQty !== originalQty;
  const hasLotExpEdit = item.editedLotNumber !== undefined || item.editedExpDate !== undefined;
  return hasQtyChange || hasLotExpEdit;
};

const buildConfirmationPayloadItem = (item: any) => {
  if (item.isSplit && item.splitUnits?.length) {
    const availableUnits = item.splitUnits.filter((unit: any) => unit.available);
    return {
      orderId: item.id,
      units: availableUnits.map((unit: any) => ({
        lotNumber: unit.lotNumber,
        expDate: unit.expDate,
      })),
    };
  }

  const originalQty = item.originalQuantity ?? item.quantity;
  const quantity = item.editedQuantity ?? item.quantity;

  return {
    orderId: item.id,
    lotNumber: item.editedLotNumber ?? (item.OrderItemSnapshot?.lotNumber ?? item.Product?.lotNumber ?? ""),
    expDate: item.editedExpDate ?? (item.OrderItemSnapshot?.expDate ?? item.Product?.expDate),
    ...(quantity !== originalQty ? { quantity } : {}),
  };
};

const expandAvailableIdsWithSplitResults = (
  availableIds: number[],
  adjustmentResults: { orderId: number; newOrderIds?: number[] }[] = []
) => {
  const expanded = [...availableIds];
  adjustmentResults.forEach((entry) => {
    if (availableIds.includes(entry.orderId) && entry.newOrderIds?.length) {
      expanded.push(...entry.newOrderIds);
    }
  });
  return expanded;
};

export const SellerConfirmationDialog: React.FunctionComponent<SellerConfirmationDialogProps> = ({ open, setOpen, setSuccessOrderId, data, refetch, orderNumber }) => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToasterContext);
  const [activeStatus, setActiveStatus] = useState<string>("CONFIRM_STATUS");
  const defaultAvailableItems = data.filter((dataItem: any) => dataItem.orderNumber === orderNumber);
  const [availableItems, setAvailableItems] = useState<any>(defaultAvailableItems);
  const [fedExInsurance, setFedExInsurance] = useState<number>(0);
  const [fedExPickupType, setFedExPickupType] = useState<string>(FedexPickupType.dropOffAtFedexLocation);
  const [fedExPickupDate, setFedExPickupDate] = useState<string>("");
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const confirmInFlightRef = useRef(false);

  const [updateManyOrder, { loading }] = useMutationUpdateManyOrder();

  const hasInvalidEdits = availableItems.some((item: any) => item.status === 1 && item.editInvalid);
  const busy = isConfirming || loading;

  // Block browser refresh / tab close while confirmation is in progress.
  useEffect(() => {
    if (!busy) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [busy]);

  // Block browser Back while confirmation is running.
  useEffect(() => {
    if (!busy) {
      return undefined;
    }

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      addToast({
        type: NotificationType.Error,
        title: "Please wait — order confirmation is still in progress.",
      });
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [busy, addToast]);

  // Hard-block pointer events on the rest of the app (sidebar uses navigate(), not <a>).
  useEffect(() => {
    if (!busy) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [busy]);

  const handleSetActive = (value: string) => {
    if (busy) {
      return;
    }
    setActiveStatus(value);
  };

  const handleNext = () => {
    if (busy) {
      return;
    }
    setActiveStatus("CONFIRM_ORDER");
  };

  const hasAvailableItems = (items: any) => {
    let isAvailableItems = false;

    items.forEach((availableItem: any) => {
      if (availableItem.status === 1) {
        isAvailableItems = true;
      }
    });

    return isAvailableItems;
  };

  const handleDialogClose = () => {
    if (busy) {
      return;
    }
    setOpen(false);
  };

  const handleConfirmOrder = async () => {
    if (confirmInFlightRef.current || busy || hasInvalidEdits) {
      return;
    }
    confirmInFlightRef.current = true;
    setIsConfirming(true);

    try {
      const outOfStockIds = availableItems.filter((item: any) => item.status === 2).map((item: any) => item.id);
      let availableIds = availableItems.filter((item: any) => item.status === 1).map((item: any) => item.id);

      const editedItems = availableItems
        .filter((item: any) => needsConfirmationDetails(item))
        .map((item: any) => buildConfirmationPayloadItem(item));

      if (editedItems.length > 0) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/v2/order/edit/confirmationDetails`,
            editedItems
          );
          availableIds = expandAvailableIdsWithSplitResults(
            availableIds,
            response.data?.data ?? []
          );
        } catch (error: any) {
          const message = error?.response?.data?.message || error.message;
          addToast({ type: NotificationType.Error, title: Array.isArray(message) ? message.join(", ") : message });
          return;
        }
      }

      const availableRequestData: any = {
        status: { set: 1 },
        isConfirmed: { set: true },
        fedExInsurance: { set: `${fedExInsurance}` },
        fedExPickupType: { set: fedExPickupType },
        shippingStatus: { set: 1 },
      };
      const outOfStockRequestData: any = {
        status: { set: 2 },
        isConfirmed: { set: true },
      };

      // if order is edited disable products
      if (data?.[0]?.loopStatus === "pending_seller_last") {
        const productIDs = availableItems.map((item: any) => item.productId);
        const disableProductApi = `${process.env.REACT_APP_API_URL}/v2/order/edit/disableProduct`;
        const disableProductRequestData = {
          productIDs,
        };
        axios
          .post(disableProductApi, disableProductRequestData)
          .then(() => {})
          .catch((error) => {
            addToast({ type: NotificationType.Error, title: error.message });
          });
      }

      if (outOfStockIds.length > 0) {
        await updateManyOrder({
          variables: {
            data: outOfStockRequestData,
            where: { id: { in: outOfStockIds } },
          },
        });
        setSuccessOrderId(null);
        if (availableIds.length === 0) {
          setOpen(false);
          refetch();
          navigate(routeConfigs.sellHistory.path);
          return;
        }
      }

      if (availableIds.length > 0) {
        await updateManyOrder({
          variables: {
            data: availableRequestData,
            where: { id: { in: availableIds } },
            pickupDate: fedExPickupDate,
          },
        });
        setOpen(false);
        setSuccessOrderId(availableIds[0]);
      }
    } catch (error: any) {
      addToast({ type: NotificationType.Error, title: error.message || "Failed to confirm order." });
    } finally {
      confirmInFlightRef.current = false;
      setIsConfirming(false);
    }
  };

  return (
    <>
      {busy &&
        createPortal(
          <Box
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirming order"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 10000,
              bgcolor: "rgba(0, 0, 0, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1.5,
              pointerEvents: "all",
              cursor: "wait",
            }}
          >
            <CircularProgress size={40} sx={{ color: "common.white" }} />
            <Box sx={{ fontWeight: 700, fontSize: 16, color: "common.white" }}>
              Confirming order… Please wait.
            </Box>
            <Box sx={{ fontSize: 13, color: "grey.300" }}>
              Do not navigate away until this finishes.
            </Box>
          </Box>,
          document.body
        )}
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={busy}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Seller Confirmation</DialogTitle>
      <DialogContent sx={{ height: "600px", position: "relative", px: 2, pt: 1.5, bgcolor: "grey.50" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, mb: 1 }}>
          <Button
            fullWidth
            variant={activeStatus === "CONFIRM_STATUS" ? "contained" : "outlined"}
            onClick={() => {
              setAvailableItems(defaultAvailableItems);
              handleSetActive("CONFIRM_STATUS");
            }}
            disabled={!hasAvailableItems(availableItems) || busy}
          >
            Confirm Status
          </Button>
          <Button
            fullWidth
            sx={{ ml: 2 }}
            variant={activeStatus === "CONFIRM_ORDER" ? "contained" : "outlined"}
            onClick={() => handleSetActive("CONFIRM_ORDER")}
            disabled={availableItems.length === 0 || !hasAvailableItems(availableItems) || busy}
          >
            Confirm Order
          </Button>
        </Box>
        <Alert
          severity="info"
          sx={{
            mb: 1,
            py: 0.75,
            color: "text.primary",
            borderLeft: "3px solid",
            borderLeftColor: "info.main",
            "& .MuiAlert-message": {
              py: 0.25,
              fontSize: 13,
              lineHeight: 1.45,
              fontWeight: 500,
            },
            "& .MuiAlert-icon": { alignItems: "center" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box>
              <Box component="span" sx={{ fontWeight: 700 }}>
                ATTENTION:{" "}
              </Box>
              {LOT_EXP_EDIT_INFO_PARAGRAPHS[0]}
            </Box>
            {LOT_EXP_EDIT_INFO_PARAGRAPHS.slice(1).map((paragraph) => (
              <Box key={paragraph}>{paragraph}</Box>
            ))}
          </Box>
        </Alert>
        {activeStatus === "CONFIRM_STATUS" && (
          <ConfirmStatus data={data.filter((dataItem: any) => dataItem.orderNumber === orderNumber)} availableItems={availableItems} setAvailableItems={setAvailableItems} />
        )}
        {activeStatus === "CONFIRM_ORDER" && hasAvailableItems(availableItems) && (
          <ConfirmOrder availableItems={availableItems} setFedExInsurance={setFedExInsurance} setFedExPickupType={setFedExPickupType} setFedExPickupDate={setFedExPickupDate} />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          fullWidth
          variant="contained"
          onClick={() => (activeStatus === "CONFIRM_STATUS" && hasAvailableItems(availableItems) ? handleNext() : handleConfirmOrder())}
          disabled={availableItems.length === 0 || busy || hasInvalidEdits}
        >
          {activeStatus === "CONFIRM_STATUS" && hasAvailableItems(availableItems) ? "Next" : "Confirm Order"}
          {(activeStatus === "CONFIRM_ORDER" || !hasAvailableItems(availableItems)) && busy && <CircularProgress sx={{ ml: 2 }} size={14} color="inherit" />}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};
